/**
 * components/charts/ExcursionChart — the MAE / MFE excursion distribution per setup,
 * as a centred-zero range chart. For each setup the trade goes offside by up to its
 * *max adverse excursion* (MAE, loss-red, extending left) before running to its *max
 * favorable excursion* (MFE, profit-green, extending right), both in R. The bar end is
 * the median (p50); a whisker tick marks the p90 tail — so the reader sees both the
 * typical swing and how bad the worst decile got.
 *
 * Two poles (adverse / favorable) with a fixed meaning, so this is a diverging form on
 * the owner-fixed loss/profit hues; a small two-item legend names them (identity is
 * never colour-alone). A shared symmetric R scale keeps left and right comparable across
 * rows. p50/p90 are n-weighted across the setup's buckets (labelled as such below).
 */
export interface ExcursionRow {
  readonly key: string;
  readonly label: string;
  readonly n: number;
  readonly maeP50: number;
  readonly maeP90: number;
  readonly mfeP50: number;
  readonly mfeP90: number;
}

interface ExcursionChartProps {
  readonly rows: readonly ExcursionRow[];
}

export function ExcursionChart({ rows }: ExcursionChartProps): JSX.Element {
  if (rows.length === 0) {
    return <div className="state"><span className="muted">No excursion data yet.</span></div>;
  }

  // Symmetric scale from the widest p90 on either side, so every row reads on one axis.
  const max = Math.max(0.5, ...rows.flatMap((r) => [r.maeP90, r.mfeP90]));
  const half = (v: number): number => Math.min(1, v / max) * 50;

  return (
    <div className="exc">
      <div className="exc__legend" aria-hidden>
        <span className="exc__key"><span className="exc__swatch exc__swatch--adv" /> Adverse (MAE)</span>
        <span className="exc__key"><span className="exc__swatch exc__swatch--fav" /> Favorable (MFE)</span>
      </div>
      <div className="exc__rows" role="img" aria-label="MAE and MFE excursion medians and p90 by setup">
        {rows.map((r) => (
          <div className="exc__row" key={r.key}>
            <span className="exc__label" title={r.label}>{r.label} <span className="muted">n={r.n}</span></span>
            <div className="exc__track">
              <span className="exc__zero" aria-hidden />
              {/* adverse — grows left from centre */}
              <span
                className="exc__bar exc__bar--adv"
                style={{ right: '50%', width: `${half(r.maeP50)}%` }}
                title={`${r.label} adverse: median −${r.maeP50.toFixed(2)}R, p90 −${r.maeP90.toFixed(2)}R`}
              />
              <span className="exc__whisker exc__whisker--adv" style={{ right: `${50 + half(r.maeP90)}%` }} aria-hidden />
              {/* favorable — grows right from centre */}
              <span
                className="exc__bar exc__bar--fav"
                style={{ left: '50%', width: `${half(r.mfeP50)}%` }}
                title={`${r.label} favorable: median +${r.mfeP50.toFixed(2)}R, p90 +${r.mfeP90.toFixed(2)}R`}
              />
              <span className="exc__whisker exc__whisker--fav" style={{ left: `${50 + half(r.mfeP90)}%` }} aria-hidden />
            </div>
            <span className="exc__val num">
              <span className="loss">−{r.maeP50.toFixed(1)}</span>
              <span className="muted"> / </span>
              <span className="profit">+{r.mfeP50.toFixed(1)}R</span>
            </span>
          </div>
        ))}
      </div>
      <div className="exc__axis">
        <span>−{max.toFixed(1)}R</span><span>0R</span><span>+{max.toFixed(1)}R</span>
      </div>
      <p className="exc__note muted">Bar = median (p50); tick = p90 tail. Values n-weighted across each setup's buckets.</p>
    </div>
  );
}
